import { UiElement } from "./UiElement.js";

export { CheckboxGroup };

/**
 * A slider based on Date objects as specialization of Slider.
 * 
 * Events:
 * * changed
 * * selected
 * * deselected
 * 
 * @author awalter <andreas.walter@awi.de>
 * @author rhess <robin.hess@awi.de>
 * 
 * @memberof vef.ui
 */

class CheckboxGroup extends UiElement {

    element = null;
    options = {
        title: 'Checkbox Group',
        nodes: [],
        multiColumn: false
    };

    constructor(target, options) {
        super(target, {
            'changed': [],
            'selected': [],
            'deselected': [],
        });

        this.nodes = [];
        this.options = { ...this.options, ...options };

        const element = this.getElement();
        element.classList.add('checkboxgroup');
        if (this.options.multiColumn) element.classList.add('multi-column');

        this.createNodes_(this.options.nodes)
    }

    /**
     * @private
     */
    createNode_(config) {
        if (!config) return;

        const element = document.createElement("div");
        element.classList.add("checkboxgroup-item");

        if (typeof config.selected != "boolean") config.selected = false;
        if (config.selected) element.classList.add('selected');

        element.innerHTML = `
            <div class="checkboxgroup-checkbox">
                <span class="fa-stack">
                    <i class="far fa-square fa-stack-1x"></i>
                    <i class="fas fa-check fa-stack-1x"></i>
                </span>
            </div>
            <div class="checkboxgroup-value" title="${config.description || config.title || ''}">
                <div class="checkboxgroup-value-inner">${config.title}</div>
            </div>
        `;

        const node = {
            element: element,
            config: config
        };

        element.addEventListener('click', (event) => {
            if (element.classList.contains("selected")) {
                this.deselect(node)
            } else {
                this.select(node)
            }
        });

        this.nodes.push(node);
        this.getElement().appendChild(element);
    }

    /**
     * @private
     */
    createNodes_(nodes) {
        const rows = (this.options.multiColumn) ? Math.ceil(nodes.length / 2) : nodes.length;
        for (let i = 0; i < rows; ++i) {
            this.createNode_(nodes[i]);
            if (this.options.multiColumn) {
                this.createNode_(nodes[i + rows]);
            }
        }
    }

    /**
     * @private
     */
    select(node) {
        if (!node.config.selected) {
            if (this.options.singleSelect) this.deselectAll();
            node.config.selected = true;
            node.element.classList.add('selected');
            this.fire('selected', node);
            this.fire('changed', node);
        }
    }

    /**
     * @private
     */
    deselect(node) {
        if (node.config.selected) {
            node.config.selected = false;
            node.element.classList.remove('selected');
            this.fire('deselected', node);
            this.fire('changed', node);
        }
    }

    /**
     * @private
     */
    selectAll() {
        const changed = [];
        for (let i = 0; i < this.nodes.length; ++i) {
            const node = this.nodes[i];
            if (!node.config.selected) {
                node.config.selected = true;
                node.element.classList.add("selected");
                this.fire('selected', node);
                changed.push(node);
            }
        }
        if (changed.length) this.fire('changed', changed);
    }

    /**
     * @private
     */
    deselectAll() {
        const changed = [];
        for (let i = 0; i < this.nodes.length; ++i) {
            const node = this.nodes[i];
            if (node.config.selected) {
                node.config.selected = false;
                node.element.classList.remove("selected");
                this.fire('deselected', node);
                changed.push(node);
            }
        }
        if (changed.length) this.fire('changed', changed);
    }

    /**
     * @private
     */
    getSelected() {
        return this.nodes.filter(node => {
            return node.config.selected;
        });
    }

    /**
     * @private
     */
    getDeselected() {
        return this.nodes.filter(node => {
            return !node.config.selected;
        });
    }
}